import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { Principal } from '@contracts/common';
import { navGroupsFor, navRowsFor } from '@/config/navigation';
import { isDesktopViewport, useSidebarState } from '@/hooks/use-sidebar-state';
import { cn } from '@/lib/utils';
import { CommandPalette } from './command-palette';
import { Header } from './header';
import { TitleSlotProvider } from './page-title-slot';
import { Sidebar } from './sidebar';

const MAIN_ID = 'noi-dung-chinh';
/** An open modal dialog (the card modal, "Việc mới", ⌘K, a question): it owns the keyboard. */
export const MODAL_OPEN = '[role="dialog"][aria-modal="true"]';
const FIELD = 'input, textarea, select, [contenteditable="true"], [contenteditable=""]';

export type AppShellProps = {
  principal: Principal;
  children: ReactNode;
  onSignOut?: () => void;
  /** The private inbox control. It is passed in rather than built here: the frame still issues no request. */
  notifications?: ReactNode;
  /** Hana (D109): the header button and the panel, built by `ProtectedShell`'s provider; absent while AI is off. */
  assistant?: AssistantSlot;
};

/**
 * The assistant's parts in the frame: its header button, its panel, whether the page makes room for it, and whether
 * the admin has missions on (the rail's "Nhiệm vụ của Hana", S7 P15).
 */
export type AssistantSlot = { open: boolean; button: ReactNode; panel: ReactNode; missions?: boolean };

/**
 * The frame every authenticated screen sits in — the HyperTek prototype's app shell (h-prototype
 * `components/layout/app-shell.tsx`): the one sidebar, the sticky glass header, the content region, and the
 * ⌘K palette mounted once. The content column clears the sidebar from lg up: 56 px collapsed, 216 px expanded.
 * The column moves at once while only the sidebar animates its width, so the Workplace board lays itself out
 * once per toggle rather than on every frame of the animation.
 *
 * It holds no data of its own and issues no request — whoever renders it has already resolved the
 * principal, and the actions it offers are callbacks the page supplies. That keeps the frame usable on a
 * screen that is still loading, and keeps a navigation change from ever firing a write.
 *
 * One deliberate difference from the prototype: the DOCUMENT scrolls, not `<main>`. The prototype pins the
 * column to `h-dvh overflow-hidden` and scrolls `<main>`; here the responsive suite measures sideways
 * overflow on the document (a scrolling `<main>` would swallow that overflow and the check would pass on a
 * page that scrolls sideways), and screens rely on document scrolling for their sticky parts (the recipient
 * register's side panel, the save bar of a long form).
 */
export function AppShell({ principal, children, onSignOut, notifications, assistant }: AppShellProps) {
  const { sidebarOpen, toggleSidebar, closeMobileDrawer } = useSidebarState();
  const toggle = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  // The banner's title slot, as state so the screen's `InTitleSlot` re-renders into it once the banner mounts.
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  // What the principal may view decides the rows (RBAC spec §5.3); a refreshed map after a 403 re-derives them.
  // One switch for the rail row and the palette entry (D158): Hana usable and missions on.
  const missions = assistant?.missions === true;
  // S8 D90: "Trợ lý của tôi" under the assistant button's own switch — a slot exists only while it is usable.
  const own = assistant !== undefined;
  const groups = useMemo(() => navGroupsFor({ permissions: principal.permissions }, { missions, assistant: own }),
    [principal.permissions, missions, own]);
  const rows = useMemo(() => navRowsFor({ permissions: principal.permissions }, { missions, assistant: own }),
    [principal.permissions, missions, own]);

  // Any route change on a phone — the drawer's own links, and also the browser's Back — leaves the drawer
  // behind rather than covering the page it opened. Only a CHANGE: the first render is the hook's to decide.
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    closeMobileDrawer();
  }, [pathname, closeMobileDrawer]);

  const closeDrawer = useCallback(() => {
    closeMobileDrawer();
    // Focus goes back to the control that opened the drawer, not to the top of the document.
    toggle.current?.focus();
  }, [closeMobileDrawer]);

  // Ctrl+\ / ⌘\ collapses and expands the sidebar, as in Notion. Not while typing in a field (a backslash is a
  // character there), and not while a modal dialog is open (the dialog owns the keyboard). `[` belongs to the
  // Workplace's "Việc lớn" block, and `/` and ⌘K to search: none of them is touched.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== '\\' || !(event.ctrlKey || event.metaKey) || event.altKey || event.defaultPrevented) return;
      if (document.querySelector(MODAL_OPEN) !== null) return;
      if (event.target instanceof Element && event.target.closest(FIELD) !== null) return;
      event.preventDefault();
      // Below the desktop the open sidebar is the drawer: shut it like Escape does, so the focus goes home.
      if (sidebarOpen && !isDesktopViewport()) closeDrawer();
      else toggleSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleSidebar, sidebarOpen, closeDrawer]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Only the phone's drawer is dismissed by Escape. On a desktop the sidebar's width is a preference, and the
    // Escape that closes a menu or a dialog bubbles up to here through React's portals.
    if (event.key !== 'Escape' || !sidebarOpen || isDesktopViewport()) return;
    closeDrawer();
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--h-background))]" onKeyDown={onKeyDown}>
      <a
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[80] focus:rounded-md focus:bg-white focus:px-4 focus:py-3 focus:font-semibold focus:text-[hsl(var(--h-primary))] focus:shadow-lg"
        href={`#${MAIN_ID}`}
      >
        Bỏ qua điều hướng
      </a>

      <Sidebar
        rows={rows}
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onClose={closeDrawer}
        onNavigate={closeMobileDrawer}
      />

      <div
        className={cn(
          'flex min-h-dvh min-w-0 flex-col',
          // Match the sidebar widths from sidebar.tsx (expanded 216 / collapsed 56). No transition: see above.
          sidebarOpen ? 'lg:pl-[216px]' : 'lg:pl-14',
          // The open panel is a 380 px column on the right from lg (D87): the page makes room rather than hide under it.
          assistant?.open === true && 'lg:pr-[380px]',
        )}
      >
        <Header
          principal={principal}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={toggleSidebar}
          toggleRef={toggle}
          onSignOut={onSignOut}
          notifications={notifications}
          assistant={assistant?.button}
          titleSlotRef={setTitleSlot}
        />
        {/* A screen that is a full-height workspace (the Workplace) marks itself `data-workspace` and takes the whole
            area under the header, edge to edge. */}
        <main id={MAIN_ID} className="min-w-0 flex-1 p-3 lg:p-4 has-[[data-workspace]]:p-0">
          <TitleSlotProvider value={titleSlot}>{children}</TitleSlotProvider>
        </main>
      </div>

      {assistant?.panel}

      {/* App-wide ⌘K palette — mounted once (an overlay, not a route). */}
      <CommandPalette groups={groups} />
    </div>
  );
}
